import { useContext, useEffect, useMemo, useRef, useState, startTransition } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  Divider,
  Avatar,
  Tooltip,
  ListSubheader,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home, Users, DollarSign, BarChart3, Settings, Moon, Sun,
  Stethoscope, UserCircle, Calendar, UserPlus, Boxes,
  UsersRound, ShieldCheck, LogOut, TrendingUp, HelpCircle, Keyboard,
  Bookmark, Store, Mail, CalendarCheck, Bed, Microscope, AlertTriangle,
  FileText, ClipboardList, Shield, Bot, Building2, ArrowRightLeft,
  Bell, Send, Package, Share2, ClipboardCheck, X, Star, Activity, FlaskConical, ShieldOff,
  ChevronsUpDown, Inbox, BookOpen,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navLabel, navSectionLabel } from '../../i18n/navLabel';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { PermissionGate } from '../PermissionGate';
import { useTenant } from '../../contexts/TenantContext';
import { useWebSocketContext } from '../../contexts/WebSocketContext';
import { ColorModeContext } from '../../App';
import { TourContext } from '../../contexts/TourContext';
import { MinimizedPatientTabs } from '../patients/MinimizedPatientTabs';
import { GlobalPatientWindows } from '../patients/GlobalPatientWindows';
import { HelpModal } from '../help/HelpModal';
import { TourMenuButton } from '../help/TourMenuButton';
import { KeyboardShortcutsModal } from '../common/KeyboardShortcutsModal';
import NotificationBell from '../common/NotificationBell';
import { BranchSwitcher } from './BranchSwitcher';
import { QuickPatientSearch } from './QuickPatientSearch';
import { design } from '../../theme/clinveaDesign';
import { AppTopBar } from './AppTopBar';
import { AiStaffChatPanel, AiChatFab } from '../ai/AiStaffChatPanel';
import { LearnMoreButton } from '../common/LearnMoreButton';
import { avatarSrc } from '../../lib/mediaUrls';
import type { StepOptions } from 'shepherd.js';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavSection {
  title?: string;
  items: {
    label: string;
    path: string;
    icon: typeof Home;
    tourId?: string;
  }[];
}

const SIDEBAR_WIDTH = 268;
const SIDEBAR_WIDTH_COLLAPSED = 76;
// Rail width below lg. Narrower than the desktop collapsed rail because phone width is scarce.
const SIDEBAR_WIDTH_RAIL = 60;
const SIDEBAR_COLLAPSED_KEY = 'staff_sidebar_collapsed';

// Scrollable but no visible scrollbar track — cross-browser (Firefox, WebKit/Blink, legacy Edge).
const hiddenScrollbarSx = {
  scrollbarWidth: 'none' as const,
  msOverflowStyle: 'none' as const,
  '&::-webkit-scrollbar': { display: 'none' },
};

// Master nav — all possible non-super-admin routes.
// The server controls which paths are visible via visiblePaths.
// section=null → top-level item; named section → grouped under a heading in the sidebar
const MASTER_NAV: Array<{ path: string; label: string; icon: typeof Home; section: string | null }> = [
  { path: '/dashboard',              label: 'Dashboard',    icon: Home,          section: null },
  { path: '/notifications',          label: 'Mail',         icon: Mail,          section: null },
  { path: '/tokens',                 label: 'Appointments', icon: Calendar,      section: 'Patient Care' },
  { path: '/patients/register',      label: 'Register',     icon: UserPlus,      section: 'Patient Care' },
  { path: '/patients',               label: 'Patients',     icon: Users,         section: 'Patient Care' },
  { path: '/referrals',              label: 'Specialist Referrals', icon: Share2, section: 'Patient Care' },
  { path: '/transfers',              label: 'Patient Transfers', icon: ArrowRightLeft, section: 'Patient Care' },
  { path: '/inpatients',             label: 'Inpatients',   icon: Bed,           section: 'Patient Care' },
  { path: '/medications',            label: 'Medications',  icon: Store,         section: 'Patient Care' },
  { path: '/billing',                label: 'Billing',      icon: DollarSign,    section: 'Patient Care' },
  { path: '/billing/queue',          label: 'Bill Queue',   icon: DollarSign,    section: 'Patient Care' },
  { path: '/follow-ups',             label: 'Follow-ups',   icon: Calendar,      section: 'Patient Care' },
  { path: '/doctor/schedule-config', label: 'Schedule',     icon: Settings,      section: 'Patient Care' },
  { path: '/doctor/appointments',    label: 'Recurring',    icon: CalendarCheck, section: 'Patient Care' },
  { path: '/doctor/notes',           label: 'Notes',        icon: FileText,      section: 'Clinical Work' },
  { path: '/doctor/prescriptions',   label: 'Rx',           icon: ClipboardList, section: 'Clinical Work' },
  { path: '/doctor/templates',       label: 'Templates',    icon: Bookmark,      section: 'Clinical Work' },
  { path: '/doctor/lab-review',      label: 'Lab AI Review', icon: FlaskConical, section: 'Clinical Work' },
  { path: '/doctor/revenue',         label: 'Revenue',      icon: TrendingUp,    section: 'Analytics & Reports' },
  { path: '/doctor/feedback',        label: 'Feedback',     icon: Star,          section: 'Analytics & Reports' },
  { path: '/reports',                label: 'Daily Report', icon: BarChart3,     section: 'Analytics & Reports' },
  { path: '/lab/tasks',              label: 'Orders',       icon: Microscope,    section: null },
  { path: '/lab/inventory',          label: 'Lab Inventory', icon: Boxes,        section: null },
  { path: '/lab/receiving',          label: 'Receiving',    icon: Inbox,         section: null },
  { path: '/pharmacy/inventory',     label: 'Medication Stock', icon: Boxes,     section: null },
  { path: '/admin/hospital-groups',  label: 'Hospital Groups', icon: Building2,   section: 'Management' },
  { path: '/admin/inventory',        label: 'Supplies & Consumables', icon: Boxes, section: 'Management' },
  { path: '/admin/staff',            label: 'Staff',        icon: UsersRound,    section: 'Management' },
  { path: '/admin/fee-schedule',     label: 'Fee Schedule', icon: DollarSign,    section: 'Management' },
  { path: '/admin/lab-catalog',      label: 'Lab Administration', icon: Microscope, section: 'Management' },
  { path: '/settings/doctors',       label: 'Doctors',      icon: Stethoscope,   section: 'Management' },
  { path: '/settings/departments',   label: 'Departments',  icon: Building2,     section: 'Management' },
  { path: '/admin/nav-settings',        label: 'Nav Settings',    icon: Shield,      section: 'Management' },
  { path: '/admin/erasure-requests',    label: 'Data Erasure',    icon: ShieldOff,   section: 'Management' },
  { path: '/admin/ai-settings',         label: 'AI Settings',     icon: Bot,         section: 'Management' },
  { path: '/admin/mcp-settings',        label: 'MCP Agents',      icon: Bot,         section: 'Management' },
  { path: '/admin/clinic-knowledge-base', label: 'Knowledge Base', icon: BookOpen,   section: 'Management' },
  { path: '/admin/clinic-profile',      label: 'Clinic Profile',  icon: Building2,   section: 'Management' },
  { path: '/admin/engagement',          label: 'Engagement',      icon: Bell,         section: 'Management' },
  { path: '/admin/checkin-settings',    label: 'Check-In',        icon: ClipboardCheck, section: 'Management' },
  { path: '/admin/health-packages',     label: 'Packages',        icon: Package,     section: 'Management' },
  { path: '/admin/corporate',           label: 'Corporate',       icon: Building2,   section: 'Management' },
  { path: '/admin/revenue-intelligence',label: 'Revenue & Reports', icon: BarChart3, section: 'Analytics & Reports' },
  { path: '/admin/feedback-nps',        label: 'Patient Feedback', icon: Star,      section: 'Analytics & Reports' },
  { path: '/admin/quality-measures',    label: 'Quality Measures', icon: Activity,  section: 'Analytics & Reports' },
  { path: '/admin/follow-ups',          label: 'Follow-ups',      icon: Bell,        section: 'Patient Reminders' },
  { path: '/admin/referrals',           label: 'Referrals',       icon: Share2,      section: 'Patient Reminders' },
  { path: '/growth',                    label: 'Follow-up List',  icon: TrendingUp,  section: 'Patient Reminders' },
  { path: '/growth/campaigns',       label: 'Bulk Reminders', icon: Send,        section: 'Patient Reminders' },
  { path: '/profile',                label: 'My Profile',   icon: UserCircle,    section: null },
];


// Super admin nav — unchanged from before, extracted as a const
const SUPER_ADMIN_NAV_SECTIONS: NavSection[] = [
  { items: [
    { label: 'Super Admin', path: '/admin',         icon: Home },
    { label: 'Dashboard',   path: '/dashboard',     icon: Home },
    { label: 'Mail',        path: '/notifications', icon: Mail },
  ]},
  { title: 'Patient Management', items: [
    { label: 'Appointments',  path: '/tokens',        icon: Calendar },
    { label: 'Patients',      path: '/patients',      icon: Users },
    { label: 'Inpatients',    path: '/inpatients',    icon: Bed },
    { label: 'Billing',       path: '/billing',       icon: DollarSign },
    { label: 'Bill Queue',    path: '/billing/queue', icon: DollarSign },
    { label: 'Follow-ups',    path: '/follow-ups',    icon: Calendar },
  ]},
  { title: 'Admin', items: [
    { label: 'Templates',          path: '/doctor/templates',         icon: Bookmark },
    { label: 'Revenue & Reports',  path: '/admin/revenue-intelligence', icon: TrendingUp },
    { label: 'Clinic Access',      path: '/admin/clinic-access',      icon: ShieldCheck },
    { label: 'Consent Policy',     path: '/admin/consent-settings',   icon: ShieldCheck },
    { label: 'Medications',        path: '/admin/medications',        icon: Store },
    { label: 'Lab Metrics',        path: '/admin/lab-metrics',        icon: Microscope },
    { label: 'Lab Administration', path: '/admin/lab-catalog',        icon: FlaskConical },
    { label: 'Inventory',          path: '/admin/inventory',          icon: Boxes },
    { label: 'Staff',              path: '/admin/staff',              icon: UsersRound },
    { label: 'Pharmacies',         path: '/admin/pharmacies',         icon: Store },
    { label: 'Pharmacy Approvals', path: '/admin/pharmacy-approvals', icon: ShieldCheck },
    { label: 'Audit Logs',         path: '/admin/audit',              icon: ShieldCheck },
    { label: 'Error Logs',         path: '/admin/error-logs',         icon: AlertTriangle },
    { label: 'Hospital Groups',    path: '/admin/hospital-groups',    icon: Building2 },
    { label: 'AI Safety Review',   path: '/admin/ai-safety-review',   icon: Shield },
    { label: 'Check-In Settings',  path: '/admin/checkin-settings',   icon: ClipboardCheck },
    { label: 'Test Data Studio',   path: '/admin/test-data',          icon: FlaskConical },
    { label: 'Data Erasure',       path: '/admin/erasure-requests',   icon: ShieldOff },
    { label: 'Settings',           path: '/settings/doctors',         icon: Settings },
  ]},
  { items: [{ label: 'My Profile', path: '/profile', icon: UserCircle }] },
];

export function Layout({ children }: LayoutProps) {
  // Bound as `tx`: this file already uses `t` for something else.
  const { t: tx } = useTranslation('ui');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, userData, clinic, signOut } = useAuth();
  const { visiblePaths, isSuperAdmin: _permSuperAdmin, canAccessPath, moduleEnabled } = usePermissions();
  const isSuperAdmin = userData?.role === 'super_admin';
  const qc = useQueryClient();
  const { subscribe } = useWebSocketContext();
  const { activeClinicId, activeClinic, isMultiClinic } = useTenant();
  const { mode, toggleColorMode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';
  // Clinical-shell tokens. Declared here, not beside sidebarGradient below, so it is
  // initialised before SidebarNavList's closure can ever read it.
  const d = design(mode);

  // Show active branch name for multi-clinic users; fall back to primary clinic name.
  //
  // super_admin is checked FIRST and separately. It is a platform-level role with clinicId null
  // and tenantScope SYSTEM — it belongs to no clinic by design — so it reached the final fallback
  // and rendered "Clinic Manager", which reads as though the platform owner is scoped to some
  // clinic. Anything clinic-shaped is wrong for this role, not just this particular wording.
  const displayClinicName = isSuperAdmin
    ? 'Clinvea Platform'
    : (isMultiClinic && activeClinic?.clinicName)
      ? activeClinic.clinicName
      : (clinic?.name || (userData?.role === 'group_admin' ? 'Hospital Group' : 'Clinic Manager'));

  // ── UI state ────────────────────────────────────────────────────────────
  const [mobileOpen,         setMobileOpen]         = useState(false);
  const [showHelpModal,      setShowHelpModal]       = useState(false);
  const [showShortcutsModal, setShowShortcutsModal]  = useState(false);
  const [showAiChat,         setShowAiChat]          = useState(false);
  const [profileAnchor,      setProfileAnchor]       = useState<HTMLElement | null>(null);
  const [collapsed,          setCollapsed]           = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1');

  // If a super_admin disables AI_ASSISTANT for this clinic while the chat panel is already open
  // (live via the PERMISSIONS_CHANGED websocket invalidation below), force it closed — hiding just
  // the toggle button isn't enough if the panel itself is already up.
  useEffect(() => {
    if (showAiChat && !isSuperAdmin && !moduleEnabled('AI_ASSISTANT')) setShowAiChat(false);
  }, [showAiChat, isSuperAdmin, moduleEnabled]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  };

  // Below lg the sidebar is always the narrow icon rail, whatever `collapsed` says — an expanded
  // 268px sidebar would swallow a phone screen. `railMode` is what the sidebar's own contents key
  // off; `collapsed` remains the user's desktop preference and is left untouched by viewport size.
  const isNarrowViewport = useMediaQuery((t: any) => t.breakpoints.down('lg'));
  const railMode = isNarrowViewport || collapsed;

  /**
   * The rail's menu button. On a narrow viewport there is nowhere to expand into, so it opens the
   * temporary Drawer overlay — which is what actually shows the labels there. On desktop it toggles
   * the persistent sidebar's width, as the old top-bar control did.
   */
  const handleMenuButton = () => {
    if (isNarrowViewport) setMobileOpen(true);
    else toggleCollapsed();
  };

  // Auto-collapse the sidebar on the Appointments screen — that page needs the width for the
  // appointment list + patient workspace. Doesn't touch the persisted preference (localStorage),
  // so it restores whatever the user had set once they navigate away.
  const collapsedBeforeAppointments = useRef<boolean | null>(null);
  useEffect(() => {
    if (location.pathname === '/tokens') {
      if (collapsedBeforeAppointments.current === null) {
        collapsedBeforeAppointments.current = collapsed;
        if (!collapsed) setCollapsed(true);
      }
    } else if (collapsedBeforeAppointments.current !== null) {
      setCollapsed(collapsedBeforeAppointments.current);
      collapsedBeforeAppointments.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const tourContext      = useContext(TourContext);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'n' && !e.shiftKey) { e.preventDefault(); startTransition(() => navigate('/tokens')); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'p' && !e.shiftKey) { e.preventDefault(); startTransition(() => navigate('/patients')); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'b' && !e.shiftKey) { e.preventDefault(); startTransition(() => navigate('/billing')); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // ── WebSocket: instant permission invalidation ──────────────────────────
  // When the super admin changes nav visibility, module access, or role permissions,
  // the backend pushes a PERMISSIONS_CHANGED message to /topic/permissions/{clinicId}
  // (and /topic/permissions/global for route-registry edits). We invalidate the
  // my-permissions cache immediately so the user's sidebar updates without waiting
  // for the 30-second staleTime to expire.
  useEffect(() => {
    if (!user?.id || isSuperAdmin) return; // super_admin nav is static; no need to react

    const invalidate = () =>
      qc.invalidateQueries({ queryKey: ['my-permissions', user.id, activeClinicId] });

    const unsubs: Array<() => void> = [];

    // Subscribe to active branch's permissions topic (falls back to primary clinic for non-group users)
    const branchId = activeClinicId ?? userData?.clinicId;
    if (branchId) {
      unsubs.push(subscribe(`/topic/permissions/${branchId}`, invalidate));
    }
    // Route-registry edits affect all clinics
    unsubs.push(subscribe('/topic/permissions/global', invalidate));

    return () => unsubs.forEach(fn => fn());
  }, [user?.id, activeClinicId, userData?.clinicId, isSuperAdmin, subscribe, qc]);

  // ── Nav sections ───────────────────────────────────────────────────────
  const navSections = useMemo<NavSection[]>(() => {
    if (isSuperAdmin) return SUPER_ADMIN_NAV_SECTIONS;

    // Setup not complete: show only setup link
    // group_admin and regional_admin have no clinic so setupCompleted is always undefined —
    // they must be exempted or they'd be permanently stuck on the setup screen.
    if (!clinic?.setupCompleted &&
        userData?.role !== 'lab_assistant' &&
        userData?.role !== 'lab_admin' &&
        userData?.role !== 'lab_staff' &&
        userData?.role !== 'pharmacy_admin' &&
        userData?.role !== 'pharmacy_staff' &&
        userData?.role !== 'group_admin' &&
        userData?.role !== 'regional_admin') {
      return [
        { items: [{ label: 'Complete Setup', path: '/setup', icon: Settings }] },
        { items: [{ label: 'My Profile',     path: '/profile', icon: UserCircle }] },
      ];
    }

    // Build from server-controlled visiblePaths
    const visibleSet = new Set<string>(visiblePaths ?? []);
    const sectionItems = new Map<string, NavSection['items']>();

    for (const item of MASTER_NAV) {
      // Patient Reminders (growth/follow-up campaigns) isn't functional for doctors — hide it
      // for that role rather than show a dead-end section.
      if (item.section === 'Patient Reminders' && userData?.role === 'doctor') continue;

      // Profile is always shown; other items must be in visiblePaths once loaded AND pass the granular
      // permission check — the server's visiblePaths can be broader than the user's actual permissions,
      // which is why items were showing for roles that couldn't use them.
      const show = item.path === '/profile' ||
        (visiblePaths !== null && visibleSet.has(item.path) && canAccessPath(item.path));
      if (!show) continue;

      const key = item.section ?? '__top__';
      if (!sectionItems.has(key)) sectionItems.set(key, []);
      sectionItems.get(key)!.push({ label: item.label, path: item.path, icon: item.icon });
    }

    const result: NavSection[] = [];
    const topItems = sectionItems.get('__top__');
    if (topItems?.length) result.push({ items: topItems });
    for (const [key, items] of sectionItems) {
      if (key === '__top__' || !items.length) continue;
      result.push({ title: key, items });
    }
    return result;
  }, [isSuperAdmin, visiblePaths, clinic?.setupCompleted, userData?.role, canAccessPath]);

  const allNavItems = useMemo(() => navSections.flatMap(s => s.items), [navSections]);

  // Path → label for the top bar's crumb. Built from the SAME nav items the sidebar renders, so a
  // route renamed in MASTER_NAV cannot end up with a crumb that disagrees with its nav entry.
  const navLabelsByPath = useMemo(
    () => new Map(allNavItems.map(i => [i.path, navLabel(t, i.label)] as const)),
    [allNavItems, t],
  );

  // All nav items live in the sidebar — including the untitled top-level ones (Dashboard, Mail,
  // Orders, Inventory) and the high-frequency ones (Appointments, Register, Patients) that used to
  // be promoted into the top bar. The top bar is utility-only now (search, notifications, profile).
  const sidebarSections = useMemo(
    () => navSections.filter(s => s.items.length > 0),
    [navSections],
  );

  const mobileSections = sidebarSections;

  // ── Tour ────────────────────────────────────────────────────────────────
  const tourTargetsByPath: Record<string, string> = {
    '/dashboard':         'nav-dashboard',
    '/tokens':            'nav-appointments',
    '/patients':          'nav-patients',
    '/patients/register': 'nav-patients-register',
    '/admin/staff':       'nav-staff',
  };

  const tourSteps = useMemo<StepOptions[]>(() => {
    const paths = new Set(allNavItems.map(i => i.path));
    const steps: StepOptions[] = [];
    const add = (id: string, target: string, text: string) =>
      steps.push({ id, text, attachTo: { element: target, on: 'bottom' } });
    if (paths.has('/dashboard'))         add('dashboard',    '[data-tour="nav-dashboard"]',         'This is your dashboard overview.');
    if (paths.has('/tokens'))            add('appointments', '[data-tour="nav-appointments"]',       'Create and manage appointments here.');
    if (paths.has('/patients') || paths.has('/patients/register'))
      add('patients', paths.has('/patients') ? '[data-tour="nav-patients"]' : '[data-tour="nav-patients-register"]', 'Add patients and keep their profiles up to date here.');
    if (paths.has('/admin/staff'))       add('staff',        '[data-tour="nav-staff"]',             'Invite staff members and manage roles from this section.');
    return steps;
  }, [allNavItems]);

  useEffect(() => {
    if (!tourContext || tourSteps.length === 0) return;
    tourContext.registerTourGuide({
      id: 'onboarding', title: 'Getting Started',
      description: 'Learn the basics of using this application',
      category: 'getting-started',
      steps: tourSteps,
    });
    // Auto-start the tour ONCE for first-run users — it was purely opt-in behind the help FAB,
    // so new admins/staff never discovered it. One-time flag; the FAB replays it any time.
    try {
      const KEY = 'clinvea_onboarding_tour_shown';
      if (!localStorage.getItem(KEY)) {
        localStorage.setItem(KEY, '1');
        setTimeout(() => tourContext.startTour('onboarding'), 1200);
      }
    } catch { /* storage unavailable — skip silently */ }
  }, [tourSteps, tourContext, userData]);

  // ── Helpers ─────────────────────────────────────────────────────────────
  const isActive = (path: string) => location.pathname === path;

  const goTo = (path: string) => {
    startTransition(() => navigate(path));
    setMobileOpen(false);
  };

  // ── Sidebar nav list — shared by the persistent desktop rail and the mobile drawer ──────
  const SidebarNavList = ({ sections, mini = false }: { sections: NavSection[]; mini?: boolean }) => (
    <List sx={{ px: 1.25, py: 0.75 }}>
      {sections.map((section, si) => (
        <Box key={si}>
          {section.title && !mini && (
            <ListSubheader sx={{
              bgcolor: 'transparent', color: d.navLabel,
              fontSize: '0.655rem', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.11em', px: 1.25, lineHeight: '2.6',
            }}>
              {navSectionLabel(t, section.title)}
            </ListSubheader>
          )}
          {mini && si > 0 && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />}
          {section.items.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const button = (
              <ListItemButton
                key={item.path}
                onClick={() => goTo(item.path)}
                data-tour={tourTargetsByPath[item.path]}
                selected={active}
                sx={{
                  borderRadius: '8px', mb: '2px', py: 1.05,
                  justifyContent: mini ? 'center' : 'flex-start',
                  color: active ? '#fff' : d.navText,
                  // Solid brand fill for the active route rather than a translucent white wash:
                  // at rail width the wash was nearly invisible against the navy.
                  '&.Mui-selected':       { bgcolor: d.brand, color: '#fff' },
                  '&.Mui-selected:hover': { bgcolor: d.brand, color: '#fff' },
                  '&:hover':              { bgcolor: d.navHover, color: '#fff' },
                }}
              >
                <ListItemIcon sx={{ minWidth: mini ? 0 : 32, justifyContent: 'center', color: 'inherit' }}>
                  <Icon size={17} />
                </ListItemIcon>
                {!mini && (
                  <ListItemText primary={navLabel(t, item.label)} primaryTypographyProps={{ fontWeight: active ? 700 : 500, fontSize: '0.86rem' }} />
                )}
              </ListItemButton>
            );
            return mini ? (
              <Tooltip key={item.path} title={navLabel(t, item.label)} placement="right">
                {button}
              </Tooltip>
            ) : button;
          })}
        </Box>
      ))}
    </List>
  );

  // ── Mobile drawer (temporary, always full-width labels) ─────────────────
  const mobileDrawerContent = (
    <Box sx={{
      width: 268, height: '100%',
      background: isDark ? 'linear-gradient(180deg, #060d1f 0%, #0b1728 100%)' : 'linear-gradient(180deg, #0f1e47 0%, #1b3582 100%)',
      color: 'white', display: 'flex', flexDirection: 'column',
    }}>
      {/* Drawer header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, py: 2.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="img"
            src="/assets/images/clinvea-logo-dark.png"
            alt="ClinVea"
            sx={{ height: 28, width: 'auto', display: 'block' }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.62rem', mt: 0.25, display: 'block' }}>
            {clinic?.clinicType || (userData?.role === 'group_admin' ? 'Group Dashboard' : 'OPD Dashboard')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setMobileOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <X size={18} />
        </IconButton>
      </Stack>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Active branch chip */}
      {isMultiClinic && activeClinic && (
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Chip
            size="small"
            icon={<Building2 size={11} />}
            label={activeClinic.clinicName}
            sx={{
              bgcolor: 'rgba(99,102,241,0.2)',
              color: 'rgba(199,210,254,0.9)',
              fontSize: '0.68rem',
              fontWeight: 600,
              border: '1px solid rgba(99,102,241,0.25)',
              '& .MuiChip-icon': { color: 'rgba(165,180,252,0.8)' },
            }}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', ...hiddenScrollbarSx }}>
        <SidebarNavList sections={mobileSections} />
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* User footer */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2.5, py: 1.5 }}>
        <Avatar
          src={userData?.avatarSmall || userData?.avatarThumbnail ? avatarSrc(userData?.id, 'small') : undefined}
          sx={{ width: 32, height: 32, bgcolor: 'rgba(99,102,241,0.8)', color: 'white', fontSize: '0.78rem', fontWeight: 700 }}
          imgProps={{ loading: 'lazy' }}
        >
          {userData?.name?.[0]?.toUpperCase() || 'U'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap>{userData?.name || 'User'}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'capitalize' }}>
            {userData?.role?.replace(/_/g, ' ') || 'Staff'}
          </Typography>
        </Box>
        <Tooltip title={tx('signOut')}>
          <IconButton onClick={signOut} size="small" sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fca5a5', bgcolor: 'rgba(239,68,68,0.15)' } }}>
            <LogOut size={15} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
  // Flat, not a gradient: this design treats the sidebar as a recessive landmark, not a brand banner.
  const sidebarGradient = d.nav;

  // ── Render ──────────────────────────────────────────────────────────────
  // No overflow rule on the outer Box below — an ancestor with overflow-x set (even just on one
  // axis) makes the browser compute overflow-y as auto too, turning it into the sticky sidebar's
  // scroll container instead of the viewport, which silently breaks `position: sticky` on it. The
  // right-hand content column has its own overflowX: hidden and isn't an ancestor of the sidebar,
  // so it's safe to clip there instead.
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>

      {/* ─── PERSISTENT SIDEBAR — all breakpoints ────────────────────────── */}
      <Box
        component="nav"
        sx={{
          // Visible at EVERY breakpoint. The top bar (and with it the hamburger) is gone, so the
          // rail is the only way to reach navigation — hiding it below lg would leave phones and
          // tablets with no way to navigate at all.
          //
          // Below lg the rail is always the narrow icon form regardless of `collapsed`: an expanded
          // 268px sidebar would cover most of a phone screen. Tapping the menu icon there opens the
          // temporary Drawer overlay instead, which is what shows the labels.
          width: {
            xs: SIDEBAR_WIDTH_RAIL,
            lg: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
          },
          flexShrink: 0, display: 'flex', flexDirection: 'column',
          position: 'sticky', top: 0, height: '100vh', zIndex: 1200,
          background: sidebarGradient, color: 'white',
          transition: 'width 0.2s ease',
        }}
      >
        {/* Brand — the collapse/expand toggle now lives in the top bar, so this row is just the
            logo (icon-only when railMode, matching a standard icon rail). */}
        <Stack
          direction="row" alignItems="center" spacing={1.25}
          sx={{ px: railMode ? 1 : 2, py: 2.25, justifyContent: railMode ? 'center' : 'flex-start' }}
        >
          {railMode ? (
            <Box
              component="img"
              src="/assets/images/clinvea-icon-cropped.png"
              alt="ClinVea"
              sx={{ height: 40, width: 40, flexShrink: 0, display: 'block' }}
            />
          ) : (
            <Box sx={{ minWidth: 0 }}>
              <Box
                component="img"
                src="/assets/images/clinvea-logo-dark.png"
                alt="ClinVea"
                sx={{ height: 60, width: 'auto', display: 'block' }}
              />
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6rem', lineHeight: 1.6, textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.25 }} noWrap>
                {displayClinicName}
              </Typography>
            </Box>
          )}
        </Stack>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Active branch chip */}
        {isMultiClinic && activeClinic && !railMode && (
          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Chip
              size="small"
              icon={<Building2 size={11} />}
              label={activeClinic.clinicName}
              sx={{
                bgcolor: 'rgba(99,102,241,0.2)', color: 'rgba(199,210,254,0.9)',
                fontSize: '0.68rem', fontWeight: 600, border: '1px solid rgba(99,102,241,0.25)',
                '& .MuiChip-icon': { color: 'rgba(165,180,252,0.8)' },
              }}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', ...hiddenScrollbarSx }}>
          <SidebarNavList sections={sidebarSections} mini={railMode} />
        </Box>

        {/*
          Utility strip — reduced to the collapse toggle. Search, branch switching, notifications,
          AI chat and the theme toggle used to live here because the app had no top bar; the
          approved design reinstates one, so those controls moved up into AppTopBar. They are
          rendered there, not duplicated here, so each still exists exactly once.
        */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={railMode ? 'center' : 'flex-start'}
          sx={{ px: railMode ? 0.5 : 1.25, py: 1, color: d.navText }}
        >
          <Tooltip title={isNarrowViewport ? 'Open navigation' : (collapsed ? 'Expand sidebar' : 'Collapse sidebar')} placement="right">
            <IconButton onClick={handleMenuButton} size="small" sx={{ color: 'inherit' }}>
              <MenuIcon size={18} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Switch Profile — reuses the same account menu the top bar's avatar used (My Profile,
            theme, shortcuts, help, sign out) so there's one menu to maintain, not two. */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <Box
          onClick={(e) => setProfileAnchor(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
            px: railMode ? 1 : 1.75, py: 1.5, justifyContent: railMode ? 'center' : 'flex-start',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
          }}
        >
          <Avatar
            src={userData?.avatarSmall || userData?.avatarThumbnail ? avatarSrc(userData?.id, 'small') : undefined}
            sx={{ width: 26, height: 26, bgcolor: 'rgba(99,102,241,0.8)', color: 'white', fontSize: '0.68rem', fontWeight: 700 }}
            imgProps={{ loading: 'lazy' }}
          >
            {userData?.name?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          {!collapsed && (
            <>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.78rem', fontWeight: 600, flex: 1 }} noWrap>
                {tx('switchProfile')}
              </Typography>
              <ChevronsUpDown size={14} style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
            </>
          )}
        </Box>
      </Box>

      {/* ─── MOBILE DRAWER ───────────────────────────────────────────────── */}
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}>
        {mobileDrawerContent}
      </Drawer>

      {/* ─── RIGHT COLUMN: content only. The top bar was removed application-wide; its
          controls now live in the sidebar utility strip. ───────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
        {/* Profile menu — triggered from the top bar */}
        <Menu
          anchorEl={profileAnchor}
          open={!!profileAnchor}
          onClose={() => setProfileAnchor(null)}
          PaperProps={{ elevation: 4, sx: { mt: 1, minWidth: 230, borderRadius: 2.5, overflow: 'hidden' } }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          {/* User info header */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
            <Typography fontWeight={700} fontSize="0.88rem" noWrap>{userData?.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {userData?.role?.replace(/_/g, ' ')}
            </Typography>
          </Box>
          <Divider />

          {/* My Profile */}
          <MenuItem
            onClick={() => { startTransition(() => navigate('/profile')); setProfileAnchor(null); }}
            sx={{ gap: 1.5, py: 1.1, fontSize: '0.85rem' }}
          >
            <UserCircle size={16} />
            {tx('myProfile')}
          </MenuItem>
          <Divider />

          {/* Theme toggle */}
          <MenuItem
            onClick={() => { toggleColorMode(); setProfileAnchor(null); }}
            sx={{ gap: 1.5, py: 1.1, fontSize: '0.85rem' }}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
            {isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          </MenuItem>

          {/* Keyboard Shortcuts */}
          <MenuItem
            onClick={() => { setShowShortcutsModal(true); setProfileAnchor(null); }}
            sx={{ gap: 1.5, py: 1.1, fontSize: '0.85rem' }}
            data-tour="help-menu"
          >
            <Keyboard size={16} />
            {tx('keyboardShortcuts')}
          </MenuItem>

          {/* Help & Tutorials */}
          <MenuItem
            onClick={() => { setShowHelpModal(true); setProfileAnchor(null); }}
            sx={{ gap: 1.5, py: 1.1, fontSize: '0.85rem' }}
          >
            <HelpCircle size={16} />
            {tx('helpAndTutorials')}
          </MenuItem>
          <Divider />

          {/* Sign out */}
          <MenuItem
            onClick={signOut}
            sx={{ gap: 1.5, py: 1.1, fontSize: '0.85rem', color: 'error.main', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}
          >
            <LogOut size={16} />
            {tx('signOut2')}
          </MenuItem>
        </Menu>

        {/* Application top bar — reinstated by the approved design. Search, branch switching,
            notifications and AI chat are passed in as children rather than re-implemented, so
            they moved here from the sidebar strip instead of being duplicated. */}
        <AppTopBar
          mode={mode}
          onToggleColorMode={toggleColorMode}
          onHelp={() => setShowHelpModal(true)}
          navLabels={navLabelsByPath}
          shiftLabel={activeClinic?.clinicName ?? null}
        >
          <QuickPatientSearch />
          <BranchSwitcher compact />
          <NotificationBell />
          <PermissionGate module="AI_ASSISTANT">
            <AiChatFab onClick={() => setShowAiChat(prev => !prev)} />
          </PermissionGate>
        </AppTopBar>

        {/* Active branch context bar — shown for multi-clinic non-super-admin users */}
        {isMultiClinic && !isSuperAdmin && activeClinic && (
          <Box sx={{
            background: isDark
              ? 'linear-gradient(to right, rgba(30,58,138,0.25), rgba(29,78,216,0.15))'
              : 'linear-gradient(to right, rgba(15,30,71,0.07), rgba(30,63,173,0.04))',
            borderBottom: `1px solid ${isDark ? 'rgba(99,102,241,0.15)' : 'rgba(30,58,138,0.1)'}`,
            px: { xs: 2, md: 3 }, py: 0.55, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 0.9, py: 0.25, borderRadius: 1, bgcolor: isDark ? 'rgba(99,102,241,0.2)' : 'rgba(30,58,138,0.08)' }}>
              <Building2 size={11} color={isDark ? '#818cf8' : '#1e3a8a'} />
              <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: isDark ? '#a5b4fc' : '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {tx('branch')}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#c7d2fe' : '#1e3a8a', fontWeight: 700, fontSize: '0.75rem' }}>
              {activeClinic.clinicName}
            </Typography>
            {(activeClinic.city || activeClinic.state) && (
              <Typography variant="caption" sx={{ color: isDark ? 'rgba(199,210,254,0.5)' : 'rgba(30,58,138,0.55)', fontSize: '0.73rem' }}>
                · {[activeClinic.city, activeClinic.state].filter(Boolean).join(', ')}
              </Typography>
            )}
          </Box>
        )}

        {/* Page content */}
        <Box
          component="main"
          sx={{
            flex: 1, minWidth: 0, width: '100%',
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: { xs: 1.5, sm: 2, md: 2 },
            pb: { xs: 3, sm: 4, md: 5 },
            // Flat, per the design's --bg. The gradient tinted the middle of every long page a
            // different colour from its top, which fought the flat white cards sitting on it.
            background: d.bg,
          }}
        >
          <Container maxWidth={false} disableGutters sx={{ width: '100%', minWidth: 0 }}>
            {children}
          </Container>
        </Box>
      </Box>

      {showHelpModal      && <HelpModal onClose={() => setShowHelpModal(false)} />}
      {showShortcutsModal && <KeyboardShortcutsModal open={showShortcutsModal} onClose={() => setShowShortcutsModal(false)} />}
      <AiStaffChatPanel open={showAiChat} onClose={() => setShowAiChat(false)} />

      <TourMenuButton />
      <LearnMoreButton />
      <GlobalPatientWindows />
      <MinimizedPatientTabs />
    </Box>
  );
}
